import { useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import MuiLink from "@mui/material/Link";
import MuiList from "@mui/material/List";
import MuiListItem from "@mui/material/ListItem";
import { cleanProps } from "../internal/cleanProps";
import { tokens } from "../tokens";
import type { ErrorSummaryProps } from "../types";

/**
 * Not generic upstream in a way this adapter exposes (composed from `Box`,
 * `List`/`ListItem` and a same-document `Link` this file builds itself —
 * never the barrel's own, route-navigating `Link`, see below) — no
 * containment note beyond "no Material UI type crosses the boundary"
 * applies.
 *
 * Mirrors docs/ux-screens/PortalErrorSummary.dc.html exactly: takes focus
 * when it appears (and again whenever its item set changes), and is
 * deliberately NOT an `aria-live` region — the prototype's own comment:
 * "focus and aria-live compete, and some screen readers cut the first
 * announcement short." Each item's anchor target is built here from the
 * caller's `fieldId` alone (never a raw `href`): the rendered target is
 * always `#${fieldId}`, so the value can only ever resolve to an in-page
 * fragment, never a constructed external or scheme URL, whatever the
 * caller passes as `fieldId`.
 */
export function ErrorSummary({ items, id, testId }: Readonly<ErrorSummaryProps>) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const itemsKey = items.map((item) => item.text).join("|");

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      panelRef.current?.focus();
    });
    return () => cancelAnimationFrame(frame);
    // Keyed on the items' rendered text (itemsKey), not on the array
    // identity, so a same-content re-render does not steal focus again.
  }, [itemsKey]);

  return (
    <Box
      ref={panelRef}
      tabIndex={-1}
      {...cleanProps({ id, "data-testid": testId })}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: `${tokens.spacing.xs}px`,
        padding: `${tokens.spacing.sm}px ${tokens.spacing.md}px`,
        backgroundColor: tokens.colour.status.error.surface,
        borderLeft: `4px solid ${tokens.colour.status.error.text}`,
        borderRadius: 0,
      }}
    >
      {/* H2's PortalErrorSummary title: 17px bold ink at the body's line height. Kept a heading
          for screen readers. */}
      <Box
        component="h2"
        sx={{ margin: 0, fontSize: 17, fontWeight: tokens.typography.weights.bold, lineHeight: "inherit", color: tokens.colour.neutral.ink }}
      >
        There is a problem
      </Box>
      <MuiList
        disablePadding
        sx={{
          listStyle: "none",
          display: "flex",
          flexDirection: "column",
          gap: `${tokens.spacing.xs}px`,
        }}
      >
        {items.map((item) => (
          <MuiListItem key={item.fieldId} disableGutters disablePadding sx={{ display: "list-item" }}>
            <MuiLink
              href={`#${item.fieldId}`}
              sx={{ color: tokens.colour.status.error.text, fontWeight: tokens.typography.weights.bold }}
            >
              {item.text}
            </MuiLink>
          </MuiListItem>
        ))}
      </MuiList>
    </Box>
  );
}
